import { api } from "../api/client.ts";
import type { FolderOut, ObjEntity, PageItemRow, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import type { Action } from "../lib/actions.ts";
import { useConversations, useListView, useUnmatchedFolders } from "../lib/cardView.ts";
import { useConversation } from "../lib/conversation.ts";
import { useEntities } from "../lib/data.ts";
import {
  crumbsOf,
  FOLDER_GROUP,
  type FolderIndex,
  type Place,
  placeOf,
  placeQuery,
  useFolderIndex,
} from "../lib/folders.ts";
import { choiceFields, itemFilterBody, itemFilters } from "../lib/itemQuery.ts";
import { ITEM_JOBS, sayDone, useJobs } from "../lib/jobs.ts";
import { spaceApi } from "../lib/paths.ts";
import { useSpacePeople } from "../lib/people.ts";
import { useListScreen } from "../lib/remember.ts";
import { type Resource, useResource } from "../lib/resource.ts";
import { canEdit } from "../lib/roles.ts";
import { location, setQuery } from "../lib/router.ts";
import { useSelection } from "../lib/selection.ts";
import type { PageProps } from "../lib/state.ts";
import { JobProgress } from "../ui/jobProgress.tsx";
import { Async, EmptyState, Loading } from "../ui/misc.tsx";
import { Paged } from "../ui/Pager.tsx";
import { SelectionBar, type Shown } from "../ui/selection.tsx";
import { Table } from "../ui/Table.tsx";
import { FolderCards } from "./folders/cards.tsx";
import { FolderConversation } from "./folders/conversation.tsx";
import { DROP_AROUND, useDropInto } from "./folders/drop.ts";
import { FolderHeader } from "./folders/header.tsx";
import { type Listed, narrowing, useFolder, useListedFolders } from "./folders/listing.ts";
import { FolderRows } from "./folders/rows.tsx";
import { FolderDrop } from "./folders/upload.tsx";
import { folderTarget, topTarget } from "./folders/uploads.ts";
import { Board, FolderBoard } from "./items/board.tsx";
import { BulkActions } from "./items/bulk.tsx";
import { ItemCards } from "./items/cards.tsx";
import { columnsBetween } from "./items/columns.ts";
import { useCreateItem } from "./items/create.tsx";
import { Filters } from "./items/filters.tsx";
import { ListBar } from "./items/listBar.tsx";
import { ItemsNav } from "./items/nav.tsx";
import { ItemsHead, Rows } from "./items/rows.tsx";

const PAGE_SIZE = 50;

/** What the list works with: the space, its entities, and the place it shows (a folder's, once loaded). */
interface Env {
  space: SpaceOut;
  entities: readonly ObjEntity[];
  place: Place;
  /** The folder shown (null at the root; undefined while it loads). */
  folder: FolderOut | null | undefined;
}

/** The list's items: its folders first (as rows, or cards), then its items, a page at a time; or a board. */
function ItemsList({ space, entities: all, place, folder }: Env) {
  const sp = location.value.searchParams;
  const sort = sp.get("sort") ?? "-created";
  // A screen per entity or kind picked in the sidebar: each keeps its filters and view.
  const screen = useListScreen("items", space.id);
  const [chosen, setView] = useListView(screen);
  const [grouped, setGrouped] = useConversations();
  const [unmatched, setUnmatched] = useUnmatchedFolders();
  // Grouping only matters where emails can be listed.
  const kind = sp.get("kind");
  // One conversation's emails are listed each, not grouped into one.
  const emails = (!kind || kind === "email") && !sp.get("thread");
  const conversations = grouped && emails ? true : null;
  const members = useSpacePeople(space);
  const filters = itemFilters(sp);
  const at = placeQuery(place);
  const list = useResource(`items:${space.id}?${sp.toString()}&c=${conversations ?? ""}`, (signal) => {
    const query = { ...filters, ...at, cursor: sp.get("cursor"), sort, limit: PAGE_SIZE, conversations };
    return api.get<PageItemRow>(spaceApi(space, "/items"), query, signal);
  });
  const index = useFolderIndex(space);
  const entity = all.find((e) => e.id === sp.get("entity"));
  const groups = choiceFields(entity);
  // "Flatten subfolders", folders without matches: where there are folders (or in one).
  const hasFolders = place.folder !== null || (index.data?.rows.length ?? 0) > 0;
  // A board has a column of the folders here (or one per folder), flattened or not.
  const listed = useListedFolders(space, chosen === "board" ? { ...place, flat: false } : place, filters, {
    first: !sp.get("cursor"),
    index: index.data,
    sort,
    unmatched,
  });
  // Grouping by folder: where the list shows folders (or in one).
  const foldersHere = place.folder !== null || (listed.rows?.length ?? 0) > 0;
  // Whether there are folders here is known (or can't be): until then a board can't say how it groups, nor what's
  // first.
  const known = (index.data !== undefined || index.error !== undefined) && listed.rows !== undefined;
  // A board needs something to group by (a choice field, or folders); without, cards.
  const view = chosen === "board" && groups.length === 0 && known && !foldersHere ? "cards" : chosen;
  // By folder when asked, or with no choice field to group by.
  const byFolder = foldersHere && (sp.get("group") === FOLDER_GROUP || groups.length === 0);
  const group = byFolder ? undefined : (groups.find((f) => f.key === sp.get("group")) ?? groups[0]);
  const folderBoard = view === "board" && byFolder;
  // Rows are selected in the table (a card is a link, a board's column a drop target), and stay so across pages,
  // searches and filters of the screen; every item the list matches can be, which a new search undoes.
  const [filter, filtered] = itemFilterBody(sp, conversations, at);
  const sel = useSelection(`${space.id}:${screen}`, JSON.stringify(filter));
  const selectable = view === "table" && canEdit(space);
  // A change to every match runs in the background: its progress where the selection's bar was, until it's done.
  const jobs = useJobs(space, ITEM_JOBS, (job) => {
    sayDone(job);
    list.reload();
  });
  const shown: Shown | undefined = list.data
    ? {
        // The first page's folders with its items (selected one by one: every match is the items').
        ids: [...(listed.rows ?? []).map((f) => f.id), ...list.data.data.map((r) => r.id)],
        total: list.data.total ?? list.data.data.length,
        filtered,
      }
    : undefined;
  // Any filter (the sidebar's entity or kind too): a list whose folders are only those holding matches.
  const narrowed = narrowing(filters);
  return (
    <>
      <Filters space={space} entity={entity} inFolder={place.folder !== null} />
      {/* The archived ones: what archiving is, and how they come back (under the pills: they stay put). */}
      {sp.get("archived") === "true" ? <p class="mb-3 text-sm text-zinc-600">{t("items.archivedHint")}</p> : null}
      <ListBar
        entity={entity}
        sort={sort}
        view={view}
        onView={setView}
        group={byFolder ? FOLDER_GROUP : group?.key}
        groups={groups}
        byFolder={foldersHere}
        // Only when there's a conversation to group (a row's thread has more emails, grouped or not).
        conversations={grouped}
        noConversations={!(emails && list.data?.data.some((r) => (r.thread_count ?? 0) > 1))}
        onConversations={(on) => {
          setGrouped(on);
          setQuery({ cursor: null });
        }}
        deep={hasFolders ? place.flat : null}
        onDeep={(on) => setQuery({ deep: String(on), cursor: null })}
        // Filtered, folders holding no match can still be listed (to file things into, say).
        unmatched={hasFolders && narrowed && (folderBoard || !place.flat) ? unmatched : null}
        onUnmatched={setUnmatched}
        selection={
          jobs.running ? (
            <JobProgress job={jobs.running} />
          ) : selectable && sel.ids.length > 0 ? (
            <BulkActions
              space={space}
              sel={sel}
              actions={["archive", "entity", "move", "rename", "request", "normalize", "delete"]}
              entities={all}
              members={members}
              archived={sp.get("archived") === "true"}
              query={shown ? { filter, total: shown.total } : undefined}
              rows={list.data?.data}
              folders={listed.rows}
              onDone={list.reload}
              onJob={jobs.follow}
              bar={(items) => <SelectionBar sel={sel} shown={shown} items={items} />}
            />
          ) : null
        }
      />
      {view === "board" && !known ? (
        // Its folders first: shown with them, not moved over when they come.
        <Loading />
      ) : folderBoard ? (
        <FolderBoard
          space={space}
          entity={entity}
          entities={all}
          sort={sort}
          members={members}
          here={place.folder}
          folders={listed.rows ?? []}
          index={index.data}
          flat={place.flat}
          unmatched={unmatched}
        />
      ) : view === "board" && entity && group ? (
        <Board
          space={space}
          entity={entity}
          field={group}
          sort={sort}
          members={members}
          // Flattened: where each is; else the folders here, a column of their own first.
          pathOf={place.flat ? (row) => (row.folder ? crumbsOf(index.data, row.folder) : undefined) : undefined}
          folders={place.flat ? undefined : listed.rows}
        />
      ) : (
        <Async r={list}>
          {(page) => (
            <Page
              env={{ space, entities: all, place, folder }}
              page={page}
              listed={listed}
              view={view}
              sel={selectable ? sel : undefined}
              onReload={list.reload}
              sort={sort}
              entity={entity}
              members={members}
              conversations={conversations === true}
              index={index.data}
              unmatched={unmatched}
            />
          )}
        </Async>
      )}
    </>
  );
}

/** One page of the list: its folders (the first page's), then its items; or, nothing in either, that it's empty. */
function Page(p: {
  env: Env;
  page: PageItemRow;
  listed: Listed;
  view: "table" | "cards" | "board";
  sel: ReturnType<typeof useSelection> | undefined;
  onReload: () => void;
  sort: string;
  entity: ObjEntity | undefined;
  members: ReturnType<typeof useSpacePeople>;
  conversations: boolean;
  index: FolderIndex | undefined;
  /** Filtered, folders holding no match listed too. */
  unmatched: boolean;
}) {
  const { space, entities } = p.env;
  const { page, listed } = p;
  const kind = location.value.searchParams.get("kind");
  const folders = listed.rows ?? [];
  // The level the list shows takes what's dropped on it outside its folders (out of a folder open in it, say).
  const here = useDropInto(space, p.env.place.folder, p.env.folder?.name ?? t("folders.root"), !canEdit(space));
  if (page.data.length === 0 && folders.length === 0) {
    // Its folders still on their way: nothing to say yet.
    return listed.rows === undefined ? null : (
      <EmptyState title={t(p.env.place.folder ? "folders.empty" : "items.empty")} />
    );
  }
  const around = p.env.folder ?? undefined;
  const env = {
    space,
    entities,
    filters: itemFilters(location.value.searchParams),
    onChange: p.onReload,
    unmatched: p.unmatched,
  };
  return (
    <Paged page={page} size={PAGE_SIZE} go={(cursor) => setQuery({ cursor })}>
      <div class={`rounded-lg ${here.over ? DROP_AROUND : ""}`} {...here.props}>
        {p.view === "cards" ? (
          <>
            <FolderCards space={space} folders={folders} pathOf={listed.pathOf} draggable={canEdit(space)} />
            <ItemCards
              space={space}
              page={page}
              conversations={p.conversations}
              entity={p.entity}
              entities={entities}
              members={p.members}
              draggable={canEdit(space)}
            />
          </>
        ) : (
          <Table>
            <ItemsHead
              entity={p.entity}
              sort={p.sort}
              onSort={(v) => setQuery({ sort: v, cursor: null })}
              emails={kind === "email"}
              select={
                p.sel ? { sel: p.sel, ids: [...folders.map((f) => f.id), ...page.data.map((r) => r.id)] } : undefined
              }
            />
            <FolderRows
              env={env}
              folders={folders}
              around={around}
              between={columnsBetween(p.entity, kind === "email")}
              sel={p.sel}
              {...(listed.pathOf ? { pathOf: listed.pathOf } : {})}
            />
            <Rows
              {...(p.sel ? { sel: p.sel } : {})}
              space={space}
              page={page}
              entity={p.entity}
              entities={entities}
              members={p.members}
              emails={kind === "email"}
              conversations={p.conversations}
              place={{
                around,
                pathOf: p.env.place.flat ? (id: string) => crumbsOf(p.index, id) : undefined,
                onChange: p.onReload,
              }}
            />
          </Table>
        )}
      </div>
    </Paged>
  );
}

/** A folder's head, and its conversation when that's the tab. */
function FolderTop({ env, r, buttons }: { env: Env; r: Resource<FolderOut | null>; buttons: readonly Action[] }) {
  const conv = useConversation(env.space, { folder: env.place.folder ?? undefined });
  const tab = location.value.searchParams.get("tab") === "conversation" ? "conversation" : "items";
  const folder = r.data;
  if (!folder) {
    return <Async r={r}>{() => null}</Async>;
  }
  return (
    <>
      <FolderHeader space={env.space} r={r} folder={folder} conv={conv} tab={tab} buttons={buttons} />
      {tab === "conversation" ? <FolderConversation space={env.space} id={folder.id} conv={conv} /> : null}
    </>
  );
}

export function Items({ space }: PageProps) {
  const sp = location.value.searchParams;
  const entities = useEntities(space);
  const place = placeOf(sp);
  const here = useFolder(space, place.folder);
  const env: Env = { space, entities: entities.data ?? [], place, folder: here.data };
  const entity = env.entities.find((e) => e.id === sp.get("entity"));
  // Picked in the sidebar: the entity's items (titled after it), or those without one.
  const title = entity?.name ?? (sp.get("entity") === "none" ? t("items.noEntity") : undefined);
  // Files dropped (or picked: "Upload files") go into the folder shown, or the list's top level: not the triage area.
  // Listing an entity's: as its (filed with no fields yet: what's missing is an issue to see to).
  const into = canEdit(space)
    ? place.folder
      ? here.data && folderTarget(space, here.data, entity)
      : topTarget(space, entity)
    : null;
  // "New": in the header's bar (a native app's too), its dialogs here.
  const create = useCreateItem({ space, entities: env.entities, folder: place.folder, uploadTo: into });
  const making = canEdit(space) ? [create.action] : [];
  if (place.folder) {
    return (
      <>
        <FolderTop env={env} r={here} buttons={making} />
        {create.dialogs}
        {/* Files dropped on its page go into it. */}
        <FolderDrop target={into ?? null} />
        {sp.get("tab") === "conversation" || !here.data ? null : <ItemsList {...env} />}
      </>
    );
  }
  return (
    <>
      <ItemsNav space={space} tab="items" entity={entity} title={title} buttons={making} />
      {create.dialogs}
      <FolderDrop target={into ?? null} />
      <ItemsList {...env} />
    </>
  );
}
